import {useSiteCheckStore} from "../Stores/SiteCheck/SiteCheck";

/**
 * The spam protection of the public forms (the server side is in config/spam.php and the form middleware):
 *  - the honeypot field: hidden from people, a robot fills it (HoneypotField.vue);
 *  - the reCAPTCHA v3 token, only where the site key and the secret are set in the settings. The script is loaded
 *    when a form is sent, not with the page; the server checks the token, its action and its site.
 */
export const HONEYPOT_FIELD = 'website_url';

type Grecaptcha = {
	ready: (callback: () => void) => void,
	execute: (key: string, options: { action: string }) => Promise<string>,
};

let script: Promise<void> | null = null;

// The site key comes with the data of the site, and only where the protection is switched on in the settings
const getSiteKey = (): string => useSiteCheckStore().currentSite?.recaptchaKey ?? '';

const loadScript = (key: string): Promise<void> => {
	script ??= new Promise<void>((resolve, reject) => {
		const element = document.createElement('script');
		element.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(key);
		element.async = true;
		element.onload = () => resolve();
		element.onerror = () => {
			script = null;
			reject(new Error('reCAPTCHA could not be loaded'));
		};
		document.head.appendChild(element);
	});

	return script;
};

const withTimeout = <T>(promise: Promise<T>, milliseconds: number): Promise<T> => Promise.race([
	promise,
	new Promise<T>((_, reject) => setTimeout(() => reject(new Error('timeout')), milliseconds)),
]);

/**
 * The token for an action, null where the protection is off or the token could not be made (the server decides then).
 */
const recaptchaToken = async (action: string): Promise<string | null> => {
	const key = getSiteKey();
	if (key === '') {
		return null;
	}

	try {
		await withTimeout(loadScript(key), 8000);
		const grecaptcha = (window as any).grecaptcha as Grecaptcha;
		await withTimeout(new Promise<void>((resolve) => grecaptcha.ready(resolve)), 8000);

		return await withTimeout(grecaptcha.execute(key, {action}), 8000);
	} catch (error) {
		return null;
	}
};

/**
 * What a public form sends besides its own fields.
 *
 * @param honeypot the value of the hidden field (empty for a person)
 * @param action   the action of the form for reCAPTCHA (form, contact_us, recall)
 */
export const protectionPayload = async (honeypot: string, action: string): Promise<Record<string, string>> => {
	const token = await recaptchaToken(action);

	return {[HONEYPOT_FIELD]: honeypot, ...(token ? {recaptcha_token: token} : {})};
};

/**
 * The message for the visitor from a failed form request: the one the server wrote for the too many attempts and the
 * failed security check, otherwise the given general one.
 */
export const formErrorMessage = (error: any, fallback: string): string => {
	const status = error?.response?.status;

	return (status === 429 || status === 422) && typeof error.response.data?.message === 'string' ? error.response.data.message : fallback;
};
